import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { storeCostInformation } from "../../Store/TemplateStore/template.action";

import InputBox from "../../components/InputBox";
import { TextFieldValidator } from "../../components/InputBox/TextFieldValidator";
import { getCurrencySymbol } from "../../utils/Utils";

import { COST_INFORMATION } from "./costInformationFields.config";

import "./index.scss";
import {
  Default_Currency_Rate,
  Language_Currency_Mapping,
} from "../home/Settings/settingsOptions.config";

const CostInformation = React.forwardRef((props, ref) => {
  const { t, i18n } = useTranslation();

  const {
    energyPrice,
    acidPrice,
    alkaliPrice,
    waterPrice,
    costofDownTime,
    additional_chemical_price,
    conversion_rate,
    currency,
    volume,
    mass,
  } = useSelector((state) => state);

  // Single source of truth: stores base (unconverted) values
  const [baseCostState, setBaseCostState] = useState({
    energyPrice,
    acidPrice,
    alkaliPrice,
    waterPrice,
    costofDownTime,
    additional_chemical_price,
  });

  // Track input display values to handle partial decimals during typing
  const [inputDisplayValues, setInputDisplayValues] = useState({});

  const [errors, setErrors] = useState({
    energyPrice: null,
    acidPrice: null,
    alkaliPrice: null,
    waterPrice: null,
    costofDownTime: null,
    additional_chemical_price: null,
  });
  const [noError, setNoError] = useState(true);
  const [costFields, setCostFields] = useState(
    COST_INFORMATION(conversion_rate, currency, volume, mass)
  );
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    const noError = Object.values(errors).every(
      (error) => error === "" || error === null
    );
    setNoError(noError);
    props.setNoCostError(noError);
  }, [errors]);

  const dispatch = useDispatch();

  // Helper function to check if value is empty or invalid
  const isEmptyOrInvalid = (value) => {
    return value === "" || value === null || value === undefined;
  };

  const calculateConvertedValue = (value) => {
    if (isEmptyOrInvalid(value) || isNaN(parseFloat(value))) return "";
    return Math.round(parseFloat(value) * conversion_rate * 100) / 100;
  };

  const calculateBaseValue = (displayValue) => {
    if (isEmptyOrInvalid(displayValue) || isNaN(parseFloat(displayValue)))
      return "";
    return parseFloat(displayValue) / conversion_rate;
  };

  // Calculate display values from base values using useMemo
  const displayCostValues = useMemo(() => {
    const fieldNames = [
      "energyPrice",
      "acidPrice",
      "alkaliPrice",
      "waterPrice",
      "costofDownTime",
      "additional_chemical_price",
    ];

    return fieldNames.reduce((acc, field) => {
      // Use input display value if currently editing, otherwise calculate from base
      acc[field] =
        inputDisplayValues[field] !== undefined
          ? inputDisplayValues[field]
          : calculateConvertedValue(baseCostState[field]);
      return acc;
    }, {});
  }, [baseCostState, conversion_rate, inputDisplayValues]);

  // Update cost fields when conversion rate, currency, volume, or mass changes
  useEffect(() => {
    setCostFields(COST_INFORMATION(conversion_rate, currency, volume, mass));
  }, [conversion_rate, currency, volume, mass]);

  // Initialize and handle state changes
  useEffect(() => {
    const newBaseCostState = {
      energyPrice: energyPrice,
      acidPrice: acidPrice,
      alkaliPrice: alkaliPrice,
      waterPrice: waterPrice,
      costofDownTime: costofDownTime,
      additional_chemical_price: additional_chemical_price,
    };

    setBaseCostState(newBaseCostState);
    setInputDisplayValues({}); // Clear any active input states

    if (initialLoad) {
      setInitialLoad(false);
    }
  }, [
    energyPrice,
    acidPrice,
    alkaliPrice,
    waterPrice,
    costofDownTime,
    additional_chemical_price,
    conversion_rate,
    initialLoad,
  ]);

  // Handle back navigation
  useEffect(() => {
    if (props.isBackClicked) {
      props.setIsBackClicked(false);
    }
  }, [props]);

  const costInformation = () => {
    if (noError) {
      // Store base (unconverted) values in Redux
      const constInformation = {
        energyPrice: baseCostState.energyPrice,
        acidPrice: baseCostState.acidPrice,
        alkaliPrice: baseCostState.alkaliPrice,
        waterPrice: baseCostState.waterPrice,
        costofDownTime: baseCostState.costofDownTime,
        additional_chemical_price: baseCostState.additional_chemical_price,
      };
      dispatch(storeCostInformation(constInformation));
    } else {
      props.setActiveStep(0);
    }
  };

  const handleInputChange = (field, displayValue, minValue, maxValue) => {
    // Update input display value immediately for UI responsiveness
    setInputDisplayValues((prev) => ({ ...prev, [field]: displayValue }));

    const fieldName = t(`costInformationStrings.${field}`).split("(")[0];

    // Handle empty input - show error and set to empty
    if (
      isEmptyOrInvalid(displayValue) ||
      (typeof displayValue === "string" && displayValue.trim() === "")
    ) {
      setErrors((prev) => ({
        ...prev,
        [field]: `${fieldName} ${t("defaultInformationStrings.required")}`,
      }));
      setBaseCostState((prev) => ({ ...prev, [field]: "" }));
      dispatch(storeCostInformation({ [field]: "" }));
      return;
    }

    // Clear errors for non-empty input
    setErrors((prev) => ({ ...prev, [field]: null }));

    const baseValue = calculateBaseValue(displayValue);
    const numValue = parseFloat(displayValue);

    // Update state and Redux for valid numbers
    setBaseCostState((prev) => ({ ...prev, [field]: baseValue }));
    dispatch(storeCostInformation({ [field]: baseValue }));

    // Validate complete numeric values

    if (minValue !== null && numValue < minValue) {
      setErrors((prev) => ({
        ...prev,
        [field]: `${fieldName} ${t(
          "defaultInformationStrings.cannotLess"
        )} ${minValue}`,
      }));
    } else if (
      maxValue !== null &&
      numValue > parseFloat(maxValue.toFixed(2))
    ) {
      setErrors((prev) => ({
        ...prev,
        [field]: `${fieldName} ${t(
          "defaultInformationStrings.cannotLess"
        )} ${minValue} ${t(
          "defaultInformationStrings.greaterThan"
        )} ${parseFloat(maxValue.toFixed(2))}`,
      }));
    }
  };

  React.useImperativeHandle(ref, () => ({ costInformation }));

  const replaceUnit = (value) => {
    const currencySymbol = getCurrencySymbol[currency];
    if (value.includes("$")) {
      if (value.includes(t("costInformationStrings.waterPrice"))) {
        value = value.replace("<unit>", volume);
      } else if (
        value.includes(t("costInformationStrings.acidPrice")) ||
        value.includes(t("costInformationStrings.alkaliPrice")) ||
        value.includes(t("costInformationStrings.additional_chemical_price"))
      ) {
        value = value.replace("<unit>", mass);
      }
      value = value.replace("$", currencySymbol);
    }
    return value;
  };

  return (
    <div className="lightGrayBorder section-container">
      <div className="headerTealText">
        {t("costInformationStrings.constInformation")}
      </div>
      <div className="grid-container ">
        {Object.keys(costFields).map((key, index) => (
          <div className="grid-item " key={index}>
            <InputBox
              value={displayCostValues[key]}
              name={key}
              labelText={replaceUnit(t(`costInformationStrings.${key}`))}
              onChange={(value) =>
                handleInputChange(
                  key,
                  value,
                  costFields[key].minValue,
                  costFields[key].maxValue
                )
              }
              validator={TextFieldValidator.NUMBER_TWO_DECIMAL}
              errorMsg={errors[key]}
              maxLength={10}
            />
          </div>
        ))}
      </div>
    </div>
  );
});

export default CostInformation;
